import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Text } from 'ui'
import { XIcon, DoubbleCopyIcon, Vicon } from './icons'
import { CenteredModal, Backdrop } from './SharedStyledComponents'
import { AcceptCouponModalProps } from './types'
import couponsImage from 'assets/png/coupons.png'
import isMobile from 'utils/isMobile'

const Container = styled(CenteredModal)`
  width: 590px;
  height: auto;
  gap: 16px;
  @media (max-width: 1081px) {
  width: 100%;
  height: 100%;
  top: 55.5%;
}
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const VisualGraphicContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
 
`

const CouponsImage = styled.img`
  width: 390px;
  height: 100%;
    @media (max-width: 1081px) {
    width: 210px;
  }
`

const InstructionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: self-start;
  width: 100%;
`

const CouponCodeContainer = styled.div`
display: flex;
padding: 16px;
flex-direction: column;
gap: 12px;
align-self: stretch;
background:  '#FFF';
border-radius: 12px;
`

const CodeButton = styled.button <{ borderColor?: string, backgroundColor?: string }>`
display: flex;
flex-direction: column;
padding: 16px;
justify-content: center;
align-items: center;
gap: 8px;
align-self: stretch;
border-radius: 8px;
background: ${props => props.backgroundColor ? props.backgroundColor : '#FFF'};
border: 1px solid ${props => props.borderColor ? props.borderColor : 'var(--Neutral-Borders-Max, #272727)'};
position: relative;
overflow: hidden;
  &:hover {
    background: #F5F5F5;
  }
`

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  height: 6px;
  width: 100%;
  background: #16AA40;
  transform: scaleX(0);
  transform-origin: right;
  animation: fillProgress 1s ease-out forwards;
  
  @keyframes fillProgress {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
`



const WarrantyTextContainer = styled.div`
  display: flex;
padding: 16px;
flex-direction: column;
gap: 10px;
align-self: stretch;
border-radius: 12px;
background: var(--Neutral-Surface-Minimal, #FFF);
margin-bottom: 96px;
`

const WarantyTextWrapper = styled.div`
display: flex;
height: 32px;
align-items: center;
gap: 16px;
align-self: stretch;
`

const ContinueButtonContainer = styled.div`
position: absolute;
bottom: 0;
left: 0;
right: 0;
display: flex;
width: 100%;
padding: 16px;
align-items: center;
gap: 18px;
border-radius: 0 0 14px 14px;
background: #FFF;
box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
@media (max-width: 1081px) {
  display: none;
}
`

const ContinueButton = styled(Button) <{ disabled?: boolean }>`
  width: 100%;
  padding: 16px;
  background: ${props => props.disabled ? '#B0B0B0' : '#007EFF'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    background: ${props => props.disabled ? '#B0B0B0' : '#0056b3'};
  }
`

const AcceptCouponModal: React.FC<AcceptCouponModalProps> = ({
  onClose,
  couponCode = 'MUI3KD',
  onContinue,
  isCodeCopied: externalIsCodeCopied,
  onCodeCopied
}) => {
  const [internalIsCodeCopied, setInternalIsCodeCopied] = useState(false)

  // Use external state if provided, otherwise use internal state
  const isCodeCopied = externalIsCodeCopied !== undefined ? externalIsCodeCopied : internalIsCodeCopied

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(couponCode)
      if (onCodeCopied) {
        onCodeCopied()
      } else {
        setInternalIsCodeCopied(true)
      }
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  const handleContinue = () => {
    if (onContinue) {
      onContinue()
    } else {
      onClose()
    }
  }

  return (
    <>
      <Backdrop onClick={onClose} />
      <Container>
        <TitleContainer>
          <Text style={{ fontSize: '24px' }} weight="bold" textcolor="#272727">הקוד שלך כאן</Text>
          <div style={{ cursor: 'pointer' }} onClick={onClose}>
            <XIcon />
          </div>
        </TitleContainer>

        <VisualGraphicContainer>
          <CouponsImage src={couponsImage} alt="Coupon process" />
        </VisualGraphicContainer>

        <InstructionsContainer>
          <Text size={isMobile() ? 'md' : 'xl'} weight="normal" textcolor="#888888">איזה כיף!</Text>
          <Text size={isMobile() ? 'xs' : 'xl'} weight="normal" textcolor="#888888">
            עכשיו אפשר להעתיק את הקוד ולהמשיך בתהליך הרכישה
          </Text>
        </InstructionsContainer>

        <CouponCodeContainer >
          <Text size={isMobile() ? 'md' : 'xl'} weight="bold" textcolor="#272727">
            הקליקו על הכפתור להעתקת קוד ההטבה
          </Text>
          <CodeButton onClick={handleCopyCode} borderColor={isCodeCopied ? '#16AA40' : 'var(--Neutral-Borders-Max, #272727)'} backgroundColor={isCodeCopied ? '#DCFCE4' : '#FFF'}>
            {isCodeCopied ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Vicon fill="#08832B" />
                <Text size="md" weight="bold" textcolor="#16652D">הקוד הועתק בהצלחה</Text>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <DoubbleCopyIcon />
                <Text size={isMobile() ? 'lg' : 'xl'} weight="bold" textcolor="#272727">{couponCode}</Text>
              </div>
            )}
            {isCodeCopied && <ProgressBar />}
          </CodeButton>
        </CouponCodeContainer>

        <WarrantyTextContainer>
          <WarantyTextWrapper>
            <Text size="sm" weight="normal" textcolor="#888888">
              אחריות היבואן בכפוף למצוין בעמוד המוצר
            </Text>
          </WarantyTextWrapper>
        </WarrantyTextContainer>

        <ContinueButtonContainer>
          <ContinueButton
            disabled={!isCodeCopied}
            onClick={isCodeCopied ? handleContinue : undefined}
          >
            <Text size="lg" weight="bold" textcolor="white">להמשך הרכישה</Text>
          </ContinueButton>
        </ContinueButtonContainer>
      </Container>
    </>
  )
}

export default AcceptCouponModal
